import { CatalogProcessor } from './CatalogProcessor';

export class Tag {

  constructor() {
    this.boundGetProcessItem = this.getProcessItem.bind(this);
  }

  /**
   * Override executeArray for Tags - includes propagation
   */
  executeArray = (items, itemsExists) => {
    // Step 1: First update tags using CatalogProcessor
    const [success, result] = CatalogProcessor.executeArray(
      items, 
      itemsExists, 
      'tags', 
      this.boundGetProcessItem,
      'label'
    );

    if (!success) {
      return [false, result];
    }

    // Step 2: Then propagate changes to other entities
    const propagatedChanges = this.propagateTagChanges(items, result.tags, itemsExists);

    // Step 3: Return tags + propagated changes
    return [true, { ...result, ...propagatedChanges }];
  }

  /**
   * Override execute for Tags - includes propagation
   */
  execute = (item, itemsExists) => {
    // Step 1: First update tag using CatalogProcessor
    const [success, result] = CatalogProcessor.execute(
      item, 
      itemsExists, 
      'tags', 
      this.boundGetProcessItem, 
      'label'
    );

    if (!success) {
      return [false, result];
    }

    // Step 2: Then propagate changes to other entities
    const propagatedChanges = this.propagateTagChanges([item], result.tags, itemsExists);

    // Step 3: Return tags + propagated changes
    return [true, { ...result, ...propagatedChanges }];
  }

  /**
   * Propagate tag changes to Units, Drivers and Assets
   * @param {Array} items - Items that were processed
   * @param {Array} updatedTags - Updated tags
   * @param {Object} itemsExists - Current state
   * @returns {Object} Propagated changes { units, drivers, assets }
   */
  propagateTagChanges = (items, updatedTags, itemsExists) => {
    const propagatedChanges = {};

    // Identify which operations were performed
    const updatedTagIds = [];
    const deletedTagIds = [];

    items.forEach(item => {
      const tagId = String(item.id || item.entityId);
      
      if (item.event === 'deleted' || item._deleted) {
        deletedTagIds.push(tagId);
      } else if (item.event === 'updated' || item.event === 'created') {
        updatedTagIds.push(tagId);
      }
    });

    // Only propagate if there are changes
    if (updatedTagIds.length === 0 && deletedTagIds.length === 0) {
      return propagatedChanges;
    }

    // Create map of updated tags for quick lookup
    const updatedTagsMap = new Map(
      updatedTags
        .filter(tag => updatedTagIds.includes(String(tag.id)))
        .map(tag => [String(tag.id), tag])
    );

    // Propagate to Units
    if (itemsExists.units) {
      propagatedChanges.units = this.propagateToEntities(
        itemsExists.units,
        updatedTagsMap,
        deletedTagIds
      );
    }

    // Propagate to Drivers
    if (itemsExists.drivers) {
      propagatedChanges.drivers = this.propagateToEntities(
        itemsExists.drivers,
        updatedTagsMap,
        deletedTagIds
      );
    }

    // Propagate to Assets
    if (itemsExists.assets) {
      propagatedChanges.assets = this.propagateToEntities(
        itemsExists.assets,
        updatedTagsMap,
        deletedTagIds
      );
    }

    return propagatedChanges;
  }

  /**
   * Propagate changes to a specific entity type
   * @param {Array} entities - Array of entities (units, drivers or assets)
   * @param {Map} updatedTagsMap - Map of updated tags
   * @param {Array} deletedTagIds - IDs of deleted tags
   * @returns {Array} Updated entities
   */
  propagateToEntities = (entities, updatedTagsMap, deletedTagIds) => {

    const updatedEntities = entities.map(entity => {
      if (!entity.tags || !Array.isArray(entity.tags)) {
        return entity;
      }

      let hasChanges = false;
      let newTags = [...entity.tags];
      let newTagIds = entity.tagIds || '';

      // Update existing tags
      if (updatedTagsMap.size > 0) {
        newTags = newTags.map(tag => {
          const tagId = String(tag.id);
          if (updatedTagsMap.has(tagId)) {
            hasChanges = true;
            return { ...tag, ...updatedTagsMap.get(tagId) };
          }
          return tag;
        });
      }

      // Remove deleted tags
      if (deletedTagIds.length > 0) {
        const beforeLength = newTags.length;
        newTags = newTags.filter(tag => !deletedTagIds.includes(String(tag.id)));
        
        if (newTags.length < beforeLength) {
          hasChanges = true;

          // Update tagIds string
          const tagIdsArray = newTagIds.split(',').filter(Boolean);
          const filteredTagIds = tagIdsArray.filter(id => !deletedTagIds.includes(id));
          newTagIds = filteredTagIds.join(',');
        }
      }

      if (hasChanges) {
        return {
          ...entity,
          tags: newTags,
          tagIds: newTagIds
        };
      }

      return entity;
    });

    return updatedEntities;
  }

  getProcessItem = (item, newItem) => {
    const objItem = {
      ...item,
      id: newItem.id !== undefined ? newItem.id : item.id,
      label: newItem.label !== undefined ? newItem.label : item.label,
      color: newItem.color !== undefined ? newItem.color : item.color,
      parentId: newItem.parentId !== undefined ? newItem.parentId : item.parentId,
      level: newItem.level !== undefined ? newItem.level : item.level,
      tagPath: newItem.tagPath !== undefined ? newItem.tagPath : item.tagPath,
      path: newItem.path !== undefined ? newItem.path : item.path,
      hasChildren: newItem.hasChildren !== undefined ? newItem.hasChildren : item.hasChildren,
      disabled: newItem.disabled !== undefined ? newItem.disabled : item.disabled,
      unitsCount: newItem.unitsCount !== undefined ? newItem.unitsCount : item.unitsCount,
      driversCount: newItem.driversCount !== undefined ? newItem.driversCount : item.driversCount
    }
    return objItem
  }

}